import { useMemo, useState } from "react";
import { createRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Route as RootRoute } from "./__root";
import { useAdminApi } from "../providers/api-context";
import { formatDateTime, jstToday, shiftDate, shiftMonth } from "../lib/format";
import { WeekStrip } from "../components/week-strip";
import { timeSlotShortLabel } from "@carry/shared";
import { Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@carry/ui";

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/status/read",
  beforeLoad: ({ context }) => {
    if (context.authLoading) return;
    if (!context.session) throw redirect({ to: "/auth/login" });
  },
  component: InstructionReadStatusPage,
});

function InstructionReadStatusPage() {
  const api = useAdminApi();
  const today = useMemo(() => jstToday(), []);
  const [planDate, setPlanDate] = useState<string>(today);

  const weekDays = useMemo(() => {
    const baseDow = new Date(`${planDate}T00:00:00`).getDay(); // 0=日 … 6=土
    return [0, 1, 2, 3, 4, 5, 6].map((o) => {
      const d = shiftDate(planDate, o - baseDow); // 日曜起点
      const dt = new Date(`${d}T00:00:00`);
      return {
        d,
        dow: ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()] ?? "",
        dom: dt.getDate(),
        isToday: d === today,
      };
    });
  }, [planDate, today]);

  const monthLabel = useMemo(() => {
    const first = weekDays[0];
    const last = weekDays[6];
    if (!first || !last) return "";
    const [fy, fm] = first.d.split("-");
    const lm = last.d.split("-")[1];
    const fmNum = Number(fm);
    const lmNum = Number(lm);
    return fmNum === lmNum ? `${fy}年${fmNum}月` : `${fy}年${fmNum}月〜${lmNum}月`;
  }, [weekDays]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["instruction-status", planDate],
    queryFn: () => api.instruction.status({ plan_date: planDate }),
    refetchInterval: 60_000,
  });

  const rows = data?.data ?? [];
  const unreadCount = rows.filter((r) => !r.is_read).length;

  return (
    <div style={{ margin: -24 }}>
      <div
        className="cp-pageheader"
        style={{
          padding: "12px 32px",
          display: "flex",
          alignItems: "center",
          gap: 24,
          borderBottom: "0.5px solid var(--rule-softer)",
          background: "var(--bg)",
        }}
      />

      <WeekStrip
        days={weekDays}
        selected={planDate}
        onSelect={setPlanDate}
        onPrev={() => setPlanDate(shiftDate(planDate, -7))}
        onNext={() => setPlanDate(shiftDate(planDate, 7))}
        onToday={() => setPlanDate(today)}
        title={monthLabel}
        onPrevMonth={() => setPlanDate(shiftMonth(planDate, -1))}
        onNextMonth={() => setPlanDate(shiftMonth(planDate, 1))}
      />

      <div style={{ padding: "20px 32px", display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)" }}>
          未確認: <strong>{unreadCount}</strong> / 全{rows.length} 件
        </div>
      </div>

      <div style={{ padding: "0 32px 32px" }}>
        {isLoading && <p style={{ color: "var(--ink-3)" }}>読み込み中…</p>}
        {error && (
          <p role="alert" style={{ color: "var(--danger)" }}>
            取得に失敗しました: {error instanceof Error ? error.message : String(error)}
          </p>
        )}
        {!isLoading && !error && rows.length === 0 && (
          <p style={{ color: "var(--ink-3)" }}>該当する配送ルートがありません</p>
        )}
        {!isLoading && rows.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ドライバー名</TableHead>
                <TableHead>ルート</TableHead>
                <TableHead style={{ textAlign: "right" }}>配送件数</TableHead>
                <TableHead>確認状況</TableHead>
                <TableHead>確認日時</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.route_id}>
                  <TableCell>{row.driver.name}</TableCell>
                  <TableCell>
                    {/* #425: 便は代表便の導出値 (nullable)。導出不能なら「-」を表示する。 */}#
                    {row.route_number} ({row.time_slot ? timeSlotShortLabel(row.time_slot) : "-"})
                  </TableCell>
                  <TableCell style={{ textAlign: "right" }}>{row.total_deliveries}</TableCell>
                  <TableCell>
                    {row.is_read ? (
                      <Badge>確認済み</Badge>
                    ) : (
                      <Badge variant="destructive">未確認</Badge>
                    )}
                  </TableCell>
                  <TableCell>{row.read_at ? formatDateTime(row.read_at) : ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
